import React, { memo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import {
  Brain,
  Sparkles,
  ShieldCheck,
  UserCheck,
  Clock,
  Zap,
  Sliders,
  Layers,
  Database,
  Search,
  ArrowRight,
  ExternalLink
} from 'lucide-react';

export interface LunaMemoryNodeData {
  memoryTitle?: string;
  memoryDepth?: 'compact' | 'standard' | 'comprehensive';
  autoRecognizeReturning?: boolean;
  trackPreferences?: boolean;
  privacyCompliance?: boolean;
  retentionDays?: number;
  [key: string]: unknown;
}

export const LunaMemoryNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as LunaMemoryNodeData;

  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData, isCompactView, openInspector } = useTenantView();

  const edges = getEdges();
  const nodes = getNodes();
  const isWired = isNodeWiredToLunaCore(id, edges, nodes);
  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';

  const updateData = (patch: Partial<LunaMemoryNodeData>) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...patch } } : n))
    );
  };

  const memoryDepth = nodeData.memoryDepth || 'standard';
  const autoRecognize = nodeData.autoRecognizeReturning !== false;
  const trackPreferences = nodeData.trackPreferences !== false;
  const retentionDays = Number(nodeData.retentionDays) || 365;

  return (
    <div
      className={`relative rounded-2xl border transition-all duration-300 shadow-2xl backdrop-blur-xl w-[320px] select-none group bg-gradient-to-b from-[#120D20]/98 via-[#090712]/98 to-[#05040B]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-purple-400 ring-2 ring-purple-500/50 shadow-[0_0_30px_rgba(168,85,247,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : isWired
          ? 'border-purple-500/40 hover:border-purple-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(168,85,247,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Directional Handles (4-Way Plug Topology) ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-memory"
        className="!w-4 !h-4 !bg-purple-400 !border-2 !border-slate-950 !-left-2 top-1/2 -translate-y-1/2 transition-transform hover:scale-150 z-30 shadow-[0_0_15px_rgba(192,132,252,1)]"
        title="Input: Connect to Luna Memory"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-memory"
        className="!w-4 !h-4 !bg-purple-400 !border-2 !border-slate-950 !-right-2 top-1/2 -translate-y-1/2 transition-transform hover:scale-150 z-30 shadow-[0_0_15px_rgba(192,132,252,1)]"
        title="Output to Luna Core Hub (Memory Ingestion)"
      />
      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className="!w-3.5 !h-3.5 !bg-purple-400 !border-2 !border-slate-950 !-top-2 left-1/2 -translate-x-1/2 transition-transform hover:scale-125 z-30 shadow-[0_0_10px_rgba(192,132,252,0.9)]"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="out-bottom"
        className="!w-3.5 !h-3.5 !bg-purple-400 !border-2 !border-slate-950 !-bottom-2 left-1/2 -translate-x-1/2 transition-transform hover:scale-125 z-30 shadow-[0_0_10px_rgba(192,132,252,0.9)]"
      />

      {/* ── Floating Cyber-HUD Action: View Live Data in Portal (Fades in on Hover) ── */}
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('customerMemoryNode', id);
            }}
            className="nodrag flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-purple-950 via-slate-900 to-indigo-950 border-2 border-purple-400 text-purple-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(168,85,247,0.8)] hover:border-purple-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
            title="Open Portal Data tab to view gathered customer memories, preferences & episodic facts"
          >
            <Database size={13} className="text-purple-300 animate-pulse" />
            <span>View Memory Data in Portal</span>
            <ArrowRight size={13} className="text-purple-400" />
          </button>
        </div>
      )}

      {/* Top Banner & Header */}
      <div className="bg-gradient-to-r from-purple-900/60 via-indigo-900/40 to-slate-900/80 p-4 border-b border-purple-500/20 rounded-t-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2.5">
            <div className="w-9 h-9 rounded-xl bg-purple-500/20 border border-purple-400/30 flex items-center justify-center text-purple-400 shadow-inner">
              <Brain className="w-5 h-5 animate-pulse text-purple-300" />
            </div>
            <div>
              <div className="flex items-center space-x-1.5">
                <span className="text-xs font-black uppercase tracking-wider text-purple-300">
                  Episodic Memory
                </span>
                <span className="px-1.5 py-0.2 text-[9px] font-bold uppercase rounded bg-purple-500/20 text-purple-300 border border-purple-400/30">
                  RAG Vault
                </span>
              </div>
              <h3 className="text-sm font-bold text-white tracking-tight">
                {nodeData.memoryTitle || 'Customer Memory Vault'}
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Body & Configuration Controls */}
      {isCompactView ? (
        <div className="p-4 space-y-2.5 text-xs">
          <div className="p-3 rounded-2xl bg-slate-900/80 border border-purple-500/25 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Brain size={14} className="text-purple-400" />
                <span className="font-bold text-white text-xs">
                  Episodic Memory Vault
                </span>
              </div>
              <button
                type="button"
                onClick={() => openInspector && openInspector(id)}
                className="nodrag px-2.5 py-1 rounded-lg text-[10px] font-bold bg-purple-500/20 hover:bg-purple-500/35 text-purple-200 border border-purple-400/40 transition cursor-pointer active:scale-95 shadow-sm"
              >
                Inspect ⚙️
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5 pt-1">
              <span className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-mono text-purple-300 font-bold capitalize">
                🧠 {memoryDepth} Depth
              </span>
              <span className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-mono text-slate-300">
                {retentionDays}d Retention
              </span>
              <span className="px-2 py-0.5 rounded-md bg-purple-950/70 border border-purple-500/30 text-[10px] font-mono text-purple-300">
                Returning Auth
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 space-y-3.5 text-xs">
        {/* Token-Optimized Memory Depth Selection */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-medium text-slate-400 flex items-center justify-between">
            <span className="flex items-center space-x-1">
              <Zap className="w-3.5 h-3.5 text-purple-400" />
              <span>Episodic Depth (Token Shield)</span>
            </span>
            <span className="text-[10px] text-purple-400 font-mono">
              {memoryDepth === 'compact' ? '~100 Tokens' : memoryDepth === 'comprehensive' ? '~500 Tokens' : '~250 Tokens'}
            </span>
          </label>
          <div className="grid grid-cols-3 gap-1.5">
            {(['compact', 'standard', 'comprehensive'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => updateData({ memoryDepth: mode })}
                className={`py-1.5 px-2 rounded-lg font-semibold text-[10px] capitalize transition-all border ${
                  memoryDepth === mode
                    ? 'bg-purple-600/30 border-purple-400 text-purple-200 shadow-sm'
                    : 'bg-slate-900/80 border-slate-800 text-slate-400 hover:border-slate-700'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>
        </div>

        {/* Feature Toggles */}
        <div className="space-y-2 pt-1 border-t border-slate-800/80">
          {/* Auto Recognize Returning Callers */}
          <label className="flex items-center justify-between cursor-pointer group">
            <div className="flex items-center space-x-2">
              <UserCheck className="w-3.5 h-3.5 text-indigo-400 group-hover:text-indigo-300" />
              <div>
                <div className="text-[11px] font-medium text-slate-200">Auto-Recognize Callers</div>
                <div className="text-[9px] text-slate-500">Cross-session greeting & name recall</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={autoRecognize}
              onChange={(e) => updateData({ autoRecognizeReturning: e.target.checked })}
              className="rounded bg-slate-900 border-slate-700 text-purple-600 focus:ring-purple-500 w-4 h-4 cursor-pointer"
            />
          </label>

          {/* Preference & Budget Tracking */}
          <label className="flex items-center justify-between cursor-pointer group">
            <div className="flex items-center space-x-2">
              <Sliders className="w-3.5 h-3.5 text-purple-400 group-hover:text-purple-300" />
              <div>
                <div className="text-[11px] font-medium text-slate-200">Track Customer Preferences</div>
                <div className="text-[9px] text-slate-500">Persist budget, brand & location facts</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={trackPreferences}
              onChange={(e) => updateData({ trackPreferences: e.target.checked })}
              className="rounded bg-slate-900 border-slate-700 text-purple-600 focus:ring-purple-500 w-4 h-4 cursor-pointer"
            />
          </label>
        </div>

        {/* Retention Period Dropdown & Custom Range */}
        <div className="pt-2 border-t border-slate-800/80 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1.5 text-slate-400">
              <Clock className="w-3.5 h-3.5 text-purple-400" />
              <span className="text-[11px]">Memory Retention:</span>
            </div>
            <select
              value={[1, 7, 14, 30, 60, 90, 180, 365, 99999].includes(retentionDays) ? retentionDays : 'custom'}
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'custom') {
                  if ([1, 7, 14, 30, 60, 90, 180, 365, 99999].includes(retentionDays)) {
                    updateData({ retentionDays: 12 });
                  }
                } else {
                  updateData({ retentionDays: Number(val) });
                }
              }}
              className="bg-slate-900 border border-slate-700/80 text-purple-300 rounded-lg px-2 py-1 text-[11px] font-medium focus:outline-none focus:border-purple-500 cursor-pointer"
            >
              <option value={1}>1 Day</option>
              <option value={7}>7 Days (1 Week)</option>
              <option value={14}>14 Days (2 Weeks)</option>
              <option value={30}>30 Days (1 Month)</option>
              <option value={60}>60 Days (2 Months)</option>
              <option value={90}>90 Days (3 Months)</option>
              <option value={180}>180 Days (6 Months)</option>
              <option value={365}>1 Year (365 Days)</option>
              <option value={99999}>Unlimited (Lifetime)</option>
              <option value="custom">Custom Days...</option>
            </select>
          </div>

          {/* Custom Days Input Field */}
          {![1, 7, 14, 30, 60, 90, 180, 365, 99999].includes(retentionDays) && (
            <div className="flex items-center justify-between bg-purple-950/40 p-2 rounded-xl border border-purple-500/30 text-[11px] animate-fadeIn">
              <span className="text-purple-200 font-medium">Custom Retention:</span>
              <div className="flex items-center space-x-1.5">
                <input
                  type="number"
                  min={1}
                  max={99999}
                  value={retentionDays}
                  onChange={(e) => updateData({ retentionDays: Math.max(1, parseInt(e.target.value) || 1) })}
                  className="w-16 bg-slate-900 border border-purple-400/50 rounded-lg px-2 py-1 text-xs text-purple-100 font-bold text-right focus:outline-none focus:ring-1 focus:ring-purple-400"
                  placeholder="Days"
                />
                <span className="text-purple-400 text-[11px] font-semibold">Days</span>
              </div>
            </div>
          )}
        </div>

        {/* Quick Jump to Portal Data Memory Studio */}
        {openPortalData && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('customerMemoryNode', id);
            }}
            className="nodrag w-full py-2 px-3 rounded-xl bg-gradient-to-r from-purple-900/60 to-indigo-900/60 hover:from-purple-800/80 hover:to-indigo-800/80 border border-purple-400/40 text-purple-200 text-xs font-bold flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer shadow-lg mt-2"
          >
            <Brain size={14} className="text-purple-300 animate-pulse" />
            <span>Open Memory Studio in Portal Data</span>
            <ArrowRight size={12} className="text-purple-400" />
          </button>
        )}

        {/* Status Indicator */}
        <div className="mt-2 pt-2 border-t border-purple-500/10 flex items-center justify-between text-[10px] text-slate-400">
          <span className="flex items-center space-x-1 text-purple-400 font-medium">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            <span>Rule 15 AI Synthesis</span>
          </span>
          <span className="font-mono text-slate-500">
            {isWired ? '🟢 Wired & Active' : '⚪ Unwired (Stateless)'}
          </span>
        </div>
      </div>
      )}
    </div>
  );
});

LunaMemoryNode.displayName = 'LunaMemoryNode';
export default LunaMemoryNode;
